import { useState, useEffect, useCallback, useRef } from 'react';
import {
    Camera,
    Save,
    AlertCircle,
    Wifi,
    WifiOff,
    Loader2,
    RefreshCw,
    Trash2,
    CheckCircle,
    Download,
    Search,
    X,
    PenLine,
    Clock,
    Eye,
    EyeOff,
} from 'lucide-react';
import { CameraComponent } from './camera-component';
import { syncService } from './sync-service';
import { cronogramaRepository } from '@/storage/cronogramaRepository';
import { photoRepository, PendingMachineCount, PhotoRecord } from '@/storage/photoRepository';
import { useProjeto } from '@/contexts/projeto-hook';
import { apiService, CronogramaTaskResponse } from '@/services/api';
import { comprimirImagem } from '@/utils/comprimir-imagem';
import { useI18n } from '@/contexts/i18n-hook';

const PREVIEW_LIMIT = 15;

const isValidMachine = (t: CronogramaTaskResponse) => {
    if (!t.nomeTarefa) return false;
    const nameLower = t.nomeTarefa.toLowerCase();

    if (/m[aá]quina\s+(xxx|\d{3})/.test(nameLower)) {
        return false;
    }

    const excluded = [
        'revisão', 'revisao', 'tramitação', 'tramitacao',
        'tramite e aprovação', 'trâmite e aprovação',
        'levantamento de campo', 'emissão', 'emissao'
    ];
    return !excluded.some(term => nameLower.includes(term));
};

interface SelectedMachine {
    id: string;
    name: string;
    isCustom?: boolean;
}

export function CapturePage() {
    const { projeto } = useProjeto();
    const { t, formatarDataHora } = useI18n();
    const [isOnline, setIsOnline] = useState(navigator.onLine);

    // States
    const [tasks, setTasks] = useState<CronogramaTaskResponse[]>([]);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [machineCounts, setMachineCounts] = useState<PendingMachineCount[]>([]);
    const [previewMachineId, setPreviewMachineId] = useState<string | null>(null);
    const [previewPhotos, setPreviewPhotos] = useState<PhotoRecord[]>([]);
    const [isLoadingPreview, setIsLoadingPreview] = useState(false);

    // Machine selector states
    const [machineQuery, setMachineQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [selectedMachine, setSelectedMachine] = useState<SelectedMachine | null>(null);
    const [recentMachines, setRecentMachines] = useState<SelectedMachine[]>([]);
    const machineInputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Sync states
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState({ current: 0, total: 0 });

    const totalPendingPhotos = machineCounts.reduce((sum, item) => sum + item.count, 0);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                machineInputRef.current && !machineInputRef.current.contains(e.target as Node) &&
                suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)
            ) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Restaurar seleção salva quando as tarefas carregarem
    useEffect(() => {
        if (!projeto?.id) return;
        const saved = localStorage.getItem(`acq_task_${projeto.id}`);
        if (!saved) return;
        try {
            const parsed = JSON.parse(saved) as SelectedMachine;
            if (parsed.id && parsed.name) {
                const stillExists = parsed.isCustom || tasks.some(t => t.id === parsed.id);
                if (stillExists || tasks.length === 0) {
                    setSelectedMachine(parsed);
                    setMachineQuery(parsed.name);
                }
            }
        } catch {
            // Formato antigo (só ID)
            if (tasks.length > 0) {
                const task = tasks.find(t => t.id === saved);
                if (task) {
                    const m = { id: task.id, name: task.nomeTarefa ?? task.id };
                    setSelectedMachine(m);
                    setMachineQuery(m.name);
                }
            }
        }
    }, [tasks, projeto?.id]);

    // Listeners de rede
    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const closePreview = useCallback(() => {
        setPreviewMachineId(null);
        setPreviewPhotos([]);
    }, []);

    const refreshPendingSummary = useCallback(async () => {
        if (!projeto?.id) return;
        const counts = await photoRepository.getPendingCountsByMachine(projeto.id);
        setMachineCounts(counts);
    }, [projeto?.id]);

    const loadPreviewForMachine = useCallback(async (maquinaId: string) => {
        if (!projeto?.id) return;
        setIsLoadingPreview(true);
        try {
            const photos = await photoRepository.getPendingPhotosByMachine(projeto.id, maquinaId, { limit: PREVIEW_LIMIT });
            setPreviewMachineId(maquinaId);
            setPreviewPhotos(photos);
        } finally {
            setIsLoadingPreview(false);
        }
    }, [projeto?.id]);

    // Carregar dados: exibe cache imediatamente, atualiza em background via API
    const loadOfflineData = useCallback(async () => {
        if (!projeto?.id) return;

        const cachedTasks = await cronogramaRepository.getTasks(projeto.id);
        if (cachedTasks) {
            setTasks(cachedTasks.filter(isValidMachine));
        }

        if (navigator.onLine) {
            try {
                const response = await apiService.listarCronograma({ projetoId: projeto.id });
                if (response.success && response.data) {
                    const apiTasks = response.data.tarefas;
                    const filtered = apiTasks.filter(isValidMachine);
                    if (filtered.length > 0) {
                        await cronogramaRepository.saveTasks(projeto.id, apiTasks);
                        setTasks(filtered);
                    }
                }
            } catch (error) {
                console.error('Falha ao atualizar máquinas da API:', error);
            }
        }

        await refreshPendingSummary();
    }, [projeto?.id, refreshPendingSummary]);

    useEffect(() => {
        loadOfflineData();
    }, [loadOfflineData]);

    // --- Combobox helpers ---

    const filteredTasks = machineQuery.trim()
        ? tasks.filter(t => {
            const q = machineQuery.toLowerCase();
            return (
                t.nomeTarefa?.toLowerCase().includes(q) ||
                t.area?.toLowerCase().includes(q) ||
                t.sismanaAntigo?.toLowerCase().includes(q)
            );
          })
        : tasks;

    const queryTrimmed = machineQuery.trim();
    const exactMatch = tasks.some(t => t.nomeTarefa?.toLowerCase() === queryTrimmed.toLowerCase());
    const showCustomOption = queryTrimmed.length > 0 && !exactMatch;

    const persistMachine = (m: SelectedMachine) => {
        if (projeto?.id) localStorage.setItem(`acq_task_${projeto.id}`, JSON.stringify(m));
    };

    const loadRecentMachines = useCallback(() => {
        if (!projeto?.id) return;
        try {
            const saved = localStorage.getItem(`acq_recent_${projeto.id}`);
            if (saved) setRecentMachines(JSON.parse(saved) as SelectedMachine[]);
        } catch {
            // ignore
        }
    }, [projeto?.id]);

    useEffect(() => {
        loadRecentMachines();
    }, [loadRecentMachines]);

    const addToRecent = useCallback((m: SelectedMachine) => {
        if (!projeto?.id) return;
        setRecentMachines(prev => {
            const deduplicated = [m, ...prev.filter(r => r.id !== m.id)];
            localStorage.setItem(`acq_recent_${projeto.id}`, JSON.stringify(deduplicated));
            return deduplicated;
        });
    }, [projeto?.id]);

    const clearRecentMachines = () => {
        if (!projeto?.id) return;
        localStorage.removeItem(`acq_recent_${projeto.id}`);
        setRecentMachines([]);
    };

    const selectTask = (task: CronogramaTaskResponse) => {
        const m: SelectedMachine = { id: task.id, name: task.nomeTarefa ?? task.id };
        setSelectedMachine(m);
        setMachineQuery(task.nomeTarefa ?? task.id);
        setShowSuggestions(false);
        persistMachine(m);
        addToRecent(m);
        closePreview();
    };

    const selectCustom = () => {
        if (!queryTrimmed) return;
        const id = `custom_${queryTrimmed.toLowerCase().replace(/[^a-z0-9]/g, '_')}_${Date.now()}`;
        const m: SelectedMachine = { id, name: queryTrimmed, isCustom: true };
        setSelectedMachine(m);
        setShowSuggestions(false);
        persistMachine(m);
        addToRecent(m);
        closePreview();
    };

    const selectRecent = (m: SelectedMachine) => {
        setSelectedMachine(m);
        setMachineQuery(m.name);
        setShowSuggestions(false);
        persistMachine(m);
        addToRecent(m);
        closePreview();
    };

    const clearMachine = () => {
        setSelectedMachine(null);
        setMachineQuery('');
        if (projeto?.id) localStorage.removeItem(`acq_task_${projeto.id}`);
        machineInputRef.current?.focus();
        closePreview();
    };

    // Handle Photo Capture
    const handleCapture = async (blob: Blob) => {
        if (!projeto?.id || !selectedMachine) return;
        try {
            // Comprime antes de guardar: economiza espaço no IndexedDB (captura offline
            // acumula muitas fotos) e deixa a sincronização mais rápida depois.
            const original =
                blob instanceof File
                    ? blob
                    : new File([blob], `captura_${selectedMachine.id}.jpg`, { type: blob.type || 'image/jpeg' });
            const foto = await comprimirImagem(original);

            await photoRepository.savePhoto({
                projetoId: projeto.id,
                maquinaId: selectedMachine.id,
                nomeMaquina: selectedMachine.name,
                foto,
                dataHora: new Date().toISOString(),
            });
            await refreshPendingSummary();
            if (previewMachineId === selectedMachine.id) {
                await loadPreviewForMachine(selectedMachine.id);
            }
        } catch (err) {
            console.error('Erro ao salvar foto', err);
            alert(t('aquisicao.mensagem.erroSalvar'));
        }
    };

    const handleDeletePhoto = async (id: string) => {
        if (confirm(t('aquisicao.mensagem.confirmarExcluir'))) {
            await photoRepository.deletePhoto(id);
            await refreshPendingSummary();
            if (previewMachineId) {
                await loadPreviewForMachine(previewMachineId);
            }
        }
    };

    const handleDownloadAll = async () => {
        if (!projeto?.id || totalPendingPhotos === 0) return;
        try {
            setIsDownloading(true);
            const pendingPhotos = await photoRepository.getPendingPhotos();
            const projectPhotos = pendingPhotos.filter(photo => photo.projetoId === projeto.id);
            const JSZipModule = await import('jszip');
            const JSZip = JSZipModule.default || JSZipModule;
            const FileSaverModule = await import('file-saver');
            const saveAs = FileSaverModule.saveAs || FileSaverModule.default;
            const zip = new JSZip();
            projectPhotos.forEach((photo, index) => {
                const machineName = photo.nomeMaquina
                    ? photo.nomeMaquina.replace(/[^a-z0-9]/gi, '_').toLowerCase()
                    : `maquina_${photo.maquinaId}`;
                const timeStr = new Date(photo.dataHora).getTime();
                const folder = zip.folder(machineName);
                if (folder) folder.file(`foto_${timeStr}_${index}.jpg`, photo.foto);
            });
            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, `fotos_projeto_${projeto.id}_${new Date().getTime()}.zip`);
        } catch (error) {
            console.error('Erro ao gerar arquivo ZIP:', error);
            alert(t('aquisicao.mensagem.erroDownload'));
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSync = async () => {
        if (!isOnline) {
            alert(t('aquisicao.mensagem.semConexaoSync'));
            return;
        }
        setIsSyncing(true);
        setSyncProgress({ current: 0, total: totalPendingPhotos });
        try {
            const result = await syncService.syncPendingPhotos((current, total) => {
                setSyncProgress({ current, total });
            });
            if (result.success > 0) {
                alert(
                    result.failed > 0
                        ? t('aquisicao.mensagem.syncSucessoComFalhas', {
                            enviadas: result.success,
                            falharam: result.failed,
                        })
                        : t('aquisicao.mensagem.syncSucesso', { enviadas: result.success })
                );
            }
            await refreshPendingSummary();
            if (previewMachineId) {
                await loadPreviewForMachine(previewMachineId);
            }
        } catch (err) {
            alert(err instanceof Error ? err.message : t('aquisicao.mensagem.erroDesconhecido'));
        } finally {
            setIsSyncing(false);
        }
    };

    const resolveMachineName = (item: PendingMachineCount) => {
        const taskInfo = tasks.find(t => t.id === item.maquinaId);
        return {
            name: taskInfo?.nomeTarefa || item.nomeMaquina || item.maquinaId,
            area: taskInfo?.area,
            isCustom: item.maquinaId.startsWith('custom_'),
        };
    };

    if (!projeto?.id) {
        return (
            <div className="p-6">
                <div className="bg-amber-50 border-2 border-amber-200 text-amber-800 rounded-lg px-4 py-3 text-sm">
                    {t('aquisicao.estado.semProjeto')}
                </div>
            </div>
        );
    }

    if (isCameraOpen) {
        return (
            <div className="fixed inset-0 z-50 bg-black flex flex-col">
                <CameraComponent
                    onCapture={handleCapture}
                    onCancel={() => setIsCameraOpen(false)}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6 max-w-4xl mx-auto pb-20">
            {/* Header */}
            <div className="bg-schmersal-blue rounded-lg shadow-sm p-6 text-white border border-schmersal-blue-light">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-2xl md:text-3xl font-bold">{t('aquisicao.cabecalho.titulo')}</h1>
                            <div aria-label={isOnline ? t('aquisicao.cabecalho.statusOnline') : t('aquisicao.cabecalho.statusOffline')} className={`px-2 py-1 rounded-full text-xs font-bold flex items-center border ${isOnline ? 'bg-green-500/20 text-green-300 border-green-500' : 'bg-red-500/20 text-red-300 border-red-500'}`}>
                                {isOnline ? <Wifi className="w-3 h-3 mr-1" /> : <WifiOff className="w-3 h-3 mr-1" />}
                                {isOnline ? t('aquisicao.cabecalho.online') : t('aquisicao.cabecalho.offline')}
                            </div>
                        </div>
                        <p className="mt-1 text-schmersal-gray-light text-sm md:text-base">
                            {t('aquisicao.cabecalho.subtitulo')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-lg shadow-sm border border-schmersal-gray-light p-6">

                {/* Seleção de Máquina — Combobox com busca + nome personalizado */}
                <div className="mb-6">
                    <label className="block text-sm font-semibold text-schmersal-gray-dark mb-2">
                        {t('aquisicao.seletor.rotulo')}
                    </label>

                    <div className="relative">
                        {/* Input de busca */}
                        <div className="relative flex items-center">
                            <Search className="absolute left-3 w-4 h-4 text-schmersal-gray-medium pointer-events-none" />
                            <input
                                ref={machineInputRef}
                                type="text"
                                value={machineQuery}
                                onChange={(e) => {
                                    setMachineQuery(e.target.value);
                                    setSelectedMachine(null);
                                    setShowSuggestions(true);
                                }}
                                onFocus={() => setShowSuggestions(true)}
                                placeholder={tasks.length > 0 ? t('aquisicao.seletor.placeholderBusca') : t('aquisicao.seletor.placeholderDigite')}
                                className={`w-full border-2 rounded-lg pl-10 pr-10 py-3 text-schmersal-gray-dark focus:outline-none focus:ring-1 transition-colors ${
                                    selectedMachine
                                        ? selectedMachine.isCustom
                                            ? 'border-amber-400 focus:border-amber-500 focus:ring-amber-500 bg-amber-50'
                                            : 'border-green-400 focus:border-green-500 focus:ring-green-500 bg-green-50'
                                        : 'border-schmersal-gray-light focus:border-schmersal-blue focus:ring-schmersal-blue'
                                }`}
                            />
                            {machineQuery && (
                                <button
                                    type="button"
                                    onClick={clearMachine}
                                    className="absolute right-3 p-1 text-schmersal-gray-medium hover:text-schmersal-gray-dark transition-colors"
                                    title={t('aquisicao.seletor.limparSelecao')}
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Badge de confirmação da seleção */}
                        {selectedMachine && (
                            <div className={`mt-2 flex items-center gap-2 text-xs px-3 py-1.5 rounded-full w-fit ${
                                selectedMachine.isCustom
                                    ? 'bg-amber-100 text-amber-800 border border-amber-300'
                                    : 'bg-green-100 text-green-800 border border-green-300'
                            }`}>
                                {selectedMachine.isCustom
                                    ? <><PenLine className="w-3 h-3" /> {t('aquisicao.seletor.badgePersonalizado')}</>
                                    : <><CheckCircle className="w-3 h-3" /> {t('aquisicao.seletor.badgeCronograma')}</>
                                }
                            </div>
                        )}

                        {/* Dropdown de sugestões */}
                        {showSuggestions && (machineQuery || tasks.length > 0 || recentMachines.length > 0) && (
                            <div
                                ref={suggestionsRef}
                                className="absolute z-20 w-full mt-1 bg-white border border-schmersal-gray-light rounded-lg shadow-lg max-h-72 overflow-y-auto"
                            >
                                {/* Seção Recentes */}
                                {recentMachines.length > 0 && (() => {
                                    const q = machineQuery.trim().toLowerCase();
                                    const visibleRecents = q
                                        ? recentMachines.filter(r => r.name.toLowerCase().includes(q))
                                        : recentMachines;
                                    if (visibleRecents.length === 0) return null;
                                    return (
                                        <div>
                                            <div className="px-3 py-1.5 text-xs font-semibold text-schmersal-gray-medium uppercase bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                                                <span className="flex items-center gap-1">
                                                    <Clock className="w-3 h-3" /> {t('aquisicao.seletor.recentes', { quantidade: visibleRecents.length })}
                                                </span>
                                                {!q && (
                                                    <button
                                                        type="button"
                                                        onMouseDown={(e) => { e.preventDefault(); clearRecentMachines(); }}
                                                        className="text-xs text-schmersal-gray-medium hover:text-red-500 flex items-center gap-0.5 transition-colors"
                                                        title={t('aquisicao.seletor.limparHistorico')}
                                                    >
                                                        <X className="w-3 h-3" /> {t('comum.acao.limpar')}
                                                    </button>
                                                )}
                                            </div>
                                            {visibleRecents.map(recent => (
                                                <button
                                                    key={recent.id}
                                                    type="button"
                                                    onMouseDown={(e) => { e.preventDefault(); selectRecent(recent); }}
                                                    className="w-full text-left px-4 py-2.5 hover:bg-schmersal-blue-light hover:text-schmersal-blue transition-colors text-sm border-b border-gray-50 last:border-0 flex items-center gap-2"
                                                >
                                                    <Clock className="w-3.5 h-3.5 flex-shrink-0 text-schmersal-gray-medium" />
                                                    <span className={recent.isCustom ? 'text-amber-700' : ''}>
                                                        {recent.name}
                                                        {recent.isCustom && <span className="ml-1.5 text-[10px] text-amber-600">{t('aquisicao.seletor.sufixoPersonalizado')}</span>}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    );
                                })()}

                                {/* Resultados do cronograma */}
                                {filteredTasks.length > 0 && (
                                    <div>
                                        {(machineQuery.trim() || recentMachines.length > 0) && (
                                            <div className="px-3 py-1.5 text-xs font-semibold text-schmersal-gray-medium uppercase bg-gray-50 border-b border-gray-100">
                                                {t('aquisicao.seletor.cronograma', { quantidade: filteredTasks.length })}
                                            </div>
                                        )}
                                        {filteredTasks.map(task => (
                                            <button
                                                key={task.id}
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); selectTask(task); }}
                                                className="w-full text-left px-4 py-2.5 hover:bg-schmersal-blue-light hover:text-schmersal-blue transition-colors text-sm border-b border-gray-50 last:border-0"
                                            >
                                                <span className="font-medium">
                                                    {task.area ? <span className="text-schmersal-gray-medium">[{task.area}] </span> : null}
                                                    {task.nomeTarefa}
                                                </span>
                                                {task.sismanaAntigo && (
                                                    <span className="ml-2 text-xs text-schmersal-gray-medium">({task.sismanaAntigo})</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Sem resultados no cronograma */}
                                {filteredTasks.length === 0 && machineQuery.trim() && (
                                    <div className="px-4 py-2.5 text-sm text-schmersal-gray-medium">
                                        {t('aquisicao.seletor.semResultados')}
                                    </div>
                                )}

                                {/* Opção de nome personalizado */}
                                {showCustomOption && (
                                    <button
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); selectCustom(); }}
                                        className="w-full text-left px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border-t border-amber-200 transition-colors"
                                    >
                                        <PenLine className="w-4 h-4 flex-shrink-0" />
                                        <span>{t('aquisicao.seletor.usarPersonalizado')} <strong>"{machineQuery.trim()}"</strong></span>
                                    </button>
                                )}

                                {/* Aviso quando nenhuma máquina carregada ainda */}
                                {tasks.length === 0 && !machineQuery.trim() && recentMachines.length === 0 && (
                                    <div className="px-4 py-3 text-sm text-schmersal-gray-medium flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-amber-500" />
                                        <span>{t('aquisicao.seletor.semMaquinas')}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Button */}
                <button
                    onClick={() => setIsCameraOpen(true)}
                    disabled={!selectedMachine}
                    className="w-full flex items-center justify-center py-4 bg-schmersal-blue text-white rounded-lg font-bold text-lg hover:bg-schmersal-blue-dark disabled:opacity-50 disabled:cursor-not-allowed shadow transition-colors"
                >
                    <Camera className="w-6 h-6 mr-2" />
                    {t('aquisicao.acao.tirarFoto')}
                </button>

            </div>

            {/* Seção de Sincronização */}
            <div className="bg-white rounded-lg shadow-sm border border-schmersal-gray-light p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-schmersal-gray-dark flex items-center">
                        <Save className="w-5 h-5 mr-2" />
                        {t('aquisicao.pendentes.titulo', { quantidade: totalPendingPhotos })}
                    </h2>

                    <div className="flex gap-2">
                        <button
                            onClick={handleDownloadAll}
                            disabled={totalPendingPhotos === 0 || isDownloading}
                            className="flex items-center px-4 py-2 bg-schmersal-blue text-white rounded font-semibold hover:bg-schmersal-blue-dark disabled:opacity-50 transition-colors"
                            title={t('aquisicao.pendentes.baixarZipTitle')}
                        >
                            {isDownloading ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4 mr-2" />
                            )}
                            <span className="hidden sm:inline">{t('aquisicao.pendentes.baixarZip')}</span>
                        </button>
                        <button
                            onClick={handleSync}
                            disabled={!isOnline || totalPendingPhotos === 0 || isSyncing}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                            {isSyncing ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            <span className="hidden sm:inline">{t('aquisicao.pendentes.sincronizar')}</span>
                        </button>
                    </div>
                </div>

                {/* Progress Bar */}
                {isSyncing && (
                    <div className="mb-4">
                        <div className="flex justify-between text-xs text-schmersal-gray-medium mb-1">
                            <span>{t('aquisicao.pendentes.enviando')}</span>
                            <span>{syncProgress.current} / {syncProgress.total}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                                className="bg-schmersal-blue h-2.5 rounded-full transition-all duration-300"
                                style={{ width: `${(syncProgress.current / Math.max(syncProgress.total, 1)) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Lista resumida por máquina */}
                {totalPendingPhotos === 0 ? (
                    <div className="text-center py-8 text-schmersal-gray-medium">
                        <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500 opacity-50" />
                        <p>{t('aquisicao.pendentes.vazio')}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {machineCounts.map((item) => {
                            const machine = resolveMachineName(item);
                            const isCurrentMachine = selectedMachine?.id === item.maquinaId;
                            const isPreviewOpen = previewMachineId === item.maquinaId;

                            return (
                                <div key={item.maquinaId} className="border border-schmersal-gray-light rounded-lg overflow-hidden">
                                    <div className="bg-gray-50 px-4 py-3 border-b border-schmersal-gray-light">
                                        <div className="font-semibold text-schmersal-gray-dark flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <span className="flex items-center gap-2">
                                                {machine.isCustom && (
                                                    <span className="text-xs font-normal px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">
                                                        {t('aquisicao.pendentes.badgePersonalizado')}
                                                    </span>
                                                )}
                                                {machine.area ? `[${machine.area}] ` : ''}
                                                {machine.name}
                                            </span>
                                            <span className="text-sm font-normal text-schmersal-gray-medium">
                                                {item.count === 1
                                                    ? t('aquisicao.pendentes.contagemSingular', { quantidade: item.count })
                                                    : t('aquisicao.pendentes.contagemPlural', { quantidade: item.count })}
                                            </span>
                                        </div>

                                        <div className="mt-3 flex items-center justify-between gap-3">
                                            <span className="text-xs text-schmersal-gray-medium">
                                                {isCurrentMachine
                                                    ? t('aquisicao.pendentes.maquinaAtual')
                                                    : t('aquisicao.pendentes.selecioneParaPreview')}
                                            </span>

                                            {isCurrentMachine ? (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        if (isPreviewOpen) {
                                                            closePreview();
                                                            return;
                                                        }
                                                        loadPreviewForMachine(item.maquinaId);
                                                    }}
                                                    className="inline-flex items-center px-3 py-1.5 rounded border border-schmersal-gray-light text-sm hover:bg-white transition-colors"
                                                >
                                                    {isPreviewOpen ? (
                                                        <>
                                                            <EyeOff className="w-4 h-4 mr-1.5" />
                                                            {t('aquisicao.pendentes.ocultarPreview')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Eye className="w-4 h-4 mr-1.5" />
                                                            {t('aquisicao.pendentes.mostrarPreview')}
                                                        </>
                                                    )}
                                                </button>
                                            ) : null}
                                        </div>
                                    </div>

                                    {isPreviewOpen && isCurrentMachine && (
                                        <div className="p-4">
                                            <div className="flex items-center justify-between mb-3">
                                                <p className="text-sm text-schmersal-gray-medium">
                                                    {t('aquisicao.preview.ultimas', { limite: PREVIEW_LIMIT })}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() => loadPreviewForMachine(item.maquinaId)}
                                                    className="text-xs inline-flex items-center px-2 py-1 rounded border border-schmersal-gray-light hover:bg-gray-50"
                                                >
                                                    <RefreshCw className="w-3 h-3 mr-1" />
                                                    {t('comum.acao.atualizar')}
                                                </button>
                                            </div>

                                            {isLoadingPreview ? (
                                                <div className="py-8 flex items-center justify-center text-schmersal-gray-medium">
                                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                                    {t('aquisicao.preview.carregando')}
                                                </div>
                                            ) : previewPhotos.length === 0 ? (
                                                <div className="py-6 text-sm text-schmersal-gray-medium text-center border border-dashed border-schmersal-gray-light rounded">
                                                    {t('aquisicao.preview.vazio')}
                                                </div>
                                            ) : (
                                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                                    {previewPhotos.map(photo => (
                                                        <div key={photo.id} className="relative group rounded border border-gray-200 overflow-hidden shadow-sm">
                                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                                            <img
                                                                src={URL.createObjectURL(photo.foto)}
                                                                alt={t('aquisicao.preview.altPendente')}
                                                                className="w-full h-32 object-cover"
                                                                onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                                                                onError={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                                                            />
                                                            <div className="p-2 bg-white">
                                                                <span className="text-[10px] text-gray-500 block text-center">
                                                                    {formatarDataHora(photo.dataHora, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
                                                                </span>
                                                            </div>
                                                            <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    onClick={() => handleDeletePhoto(photo.id)}
                                                                    className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600 shadow"
                                                                    title={t('aquisicao.preview.excluirFoto')}
                                                                >
                                                                    <Trash2 className="w-3 h-3" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
